// Closing a follow-up (client, 2026-10-28): an outcome and what the customer
// said are required, and — unless the outcome ends the chase — the next
// follow-up too. The server enforces the same rule
// (src/shared/followups/close-rules.js); this dialog just asks for it up front.
// "No next follow-up needed" skips the date, but needs a reason (2026-10-30).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { cn } from '@/shared/utils/utils';
import { lostNoteProblem } from '@/features/deals/lost-note';

export const FOLLOWUP_OUTCOMES: { value: string; label: string; tone: 'good' | 'neutral' | 'bad' }[] = [
  { value: 'interested', label: 'Interested', tone: 'good' },
  { value: 'order_expected', label: 'Order expected', tone: 'good' },
  { value: 'order_received', label: 'Order received', tone: 'good' },
  { value: 'needs_changes', label: 'Needs changes', tone: 'neutral' },
  { value: 'price_issue', label: 'Price issue', tone: 'neutral' },
  { value: 'no_response', label: 'No response', tone: 'neutral' },
  { value: 'not_interested', label: 'Not interested', tone: 'bad' },
];
export const outcomeLabel = (v?: string) => FOLLOWUP_OUTCOMES.find((o) => o.value === v)?.label || '';
const ENDS_CHASE = ['order_received', 'not_interested'];
const NO_NEXT_REASON_MIN = 10;

/** Tomorrow 11:00, as a datetime-local value. */
function defaultNext() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  d.setHours(11, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export interface CloseFollowupTarget {
  id: string;
  parentType: string;
  parentId: string;
  /** Shown in the title: the client or document. */
  title?: string;
  note?: string;
}

interface CloseResponse {
  next?: { _id: string; followupDate: string };
  lost?: { recordId: string };
  raiseOrderFor?: { id: string; number: string };
}

export function CloseFollowupDialog({ target, onClose, onClosed }: { target: CloseFollowupTarget | null; onClose: () => void; onClosed?: (r: CloseResponse) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState('');
  const [remark, setRemark] = useState('');
  const [nextAt, setNextAt] = useState(defaultNext());
  const [nextNote, setNextNote] = useState('');
  const [noNext, setNoNext] = useState(false);
  const [noNextReason, setNoNextReason] = useState('');
  const [lostReasonId, setLostReasonId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target) { setOutcome(''); setRemark(''); setNextAt(defaultNext()); setNextNote(''); setNoNext(false); setNoNextReason(''); setLostReasonId(''); setError(null); }
  }, [target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Not interested" marks the business Lost, so it takes a reason from the
  // Lost Reasons list as well (client, 2026-10-29).
  const lostReasons = useQuery({
    queryKey: ['deals', 'lost-reasons'],
    enabled: outcome === 'not_interested',
    queryFn: async () => (await api.get<{ items: Array<{ _id: string; name: string }> }>('/api/deals-outcome/lost-reasons')).data,
  });

  const canSkipNext = !!outcome && !ENDS_CHASE.includes(outcome);
  const needsNext = canSkipNext && !noNext;
  const close = useMutation({
    mutationFn: async () => (await api.patch<CloseResponse>(`/api/followups/${target!.id}`, {
      status: 'done',
      outcome,
      customerResponse: remark.trim(),
      ...(needsNext ? { next: { date: new Date(nextAt).toISOString(), note: nextNote.trim() } } : {}),
      ...(canSkipNext && noNext ? { noNext: true, noNextReason: noNextReason.trim() } : {}),
      ...(outcome === 'not_interested' ? { lostReasonId } : {}),
      parentType: target!.parentType,
      parentId: target!.parentId,
    })).data,
    onSuccess: (r) => {
      for (const key of ['followups', 'appointments', 'followups-overdue', 'followups-due-today', 'followups-upcoming', 'dashboard-kpi', 'pipeline']) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      if (r.raiseOrderFor) {
        const q = r.raiseOrderFor;
        toast.success((t) => (
          <span className="flex items-center gap-3">
            Order received — raise the order on {q.number}.
            <button type="button" className="font-semibold text-primary underline" onClick={() => { toast.dismiss(t.id); navigate(`/quotations/${q.id}`); }}>Open quotation</button>
          </span>
        ), { duration: 10000 });
      } else if (r.lost) {
        toast.success('Follow-up closed — the business is marked Lost.');
      } else {
        toast.success(r.next ? `Follow-up closed — next one on ${new Date(r.next.followupDate).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}.` : 'Follow-up closed.');
      }
      onClosed?.(r);
      onClose();
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Could not close the follow-up.')),
  });

  const submit = () => {
    if (!outcome) return setError('Choose what happened.');
    if (remark.trim().length < 3) return setError('Write what the customer said.');
    if (outcome === 'not_interested') {
      if (!lostReasonId) return setError('Choose why it was lost from the list.');
      const problem = lostNoteProblem(remark);
      if (problem) return setError(problem);
    }
    if (canSkipNext && noNext && noNextReason.trim().length < NO_NEXT_REASON_MIN) {
      return setError(`Say why no next follow-up is needed (at least ${NO_NEXT_REASON_MIN} characters).`);
    }
    if (needsNext && !nextAt) return setError('Set the next follow-up date.');
    if (needsNext && new Date(nextAt).getTime() < new Date().setHours(0, 0, 0, 0)) return setError('The next follow-up cannot be in the past.');
    setError(null);
    close.mutate();
  };

  return (
    <Modal open={!!target} onClose={onClose} size="md" title={`Close follow-up${target?.title ? ` — ${target.title}` : ''}`} footer={(
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={close.isPending}>Close follow-up</Button>
      </>
    )}>
      <div className="space-y-4">
        {target?.note && <p className="rounded-lg bg-surface-container-low px-3 py-2 text-body-sm text-on-surface-variant">{target.note}</p>}

        <div>
          <span className="mb-1.5 block text-body-sm font-medium text-on-surface">What happened? <span className="text-danger-text">*</span></span>
          <div className="flex flex-wrap gap-2">
            {FOLLOWUP_OUTCOMES.map((o) => (
              <button key={o.value} type="button" onClick={() => { setOutcome(o.value); setError(null); }}
                className={cn('rounded-full border px-3 py-1.5 text-body-sm font-semibold transition',
                  outcome === o.value
                    ? o.tone === 'bad' ? 'border-danger bg-danger/10 text-danger-text' : o.tone === 'good' ? 'border-success bg-success/10 text-success-text' : 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-on-surface-variant hover:bg-surface-container-low')}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-body-sm font-medium text-on-surface">What did the customer say? <span className="text-danger-text">*</span></span>
          <textarea value={remark} onChange={(e) => { setRemark(e.target.value); setError(null); }} rows={3}
            placeholder={outcome === 'not_interested' ? 'Why — e.g. bought from another supplier, price too high' : 'e.g. Will confirm after checking with the plant head'}
            className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>

        {canSkipNext && (
          <div className="rounded-lg border border-primary/25 bg-primary/5 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-body-sm font-semibold text-on-surface">Next follow-up {needsNext && <span className="text-danger-text">*</span>}</span>
              <label className="flex cursor-pointer items-center gap-2 text-body-sm text-on-surface-variant">
                <input type="checkbox" checked={noNext} onChange={(e) => { setNoNext(e.target.checked); setError(null); }}
                  className="h-4 w-4 rounded border-input accent-primary" />
                No next follow-up needed
              </label>
            </div>
            {needsNext ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input type="datetime-local" value={nextAt} onChange={(e) => setNextAt(e.target.value)}
                  className="h-9 rounded-lg border border-input bg-surface px-3 text-body-sm text-on-surface outline-none focus:border-primary" />
                <input value={nextNote} onChange={(e) => setNextNote(e.target.value)} placeholder="What to do next (optional)"
                  className="h-9 rounded-lg border border-input bg-surface px-3 text-body-sm text-on-surface outline-none focus:border-primary" />
              </div>
            ) : (
              <label className="block">
                <span className="mb-1.5 block text-body-sm text-on-surface">Why is no next follow-up needed? <span className="text-danger-text">*</span></span>
                <textarea value={noNextReason} onChange={(e) => { setNoNextReason(e.target.value); setError(null); }} rows={2}
                  placeholder="e.g. Customer will call us when the plant restarts in March"
                  className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              </label>
            )}
          </div>
        )}
        {outcome === 'order_received' && (
          <p className="rounded-lg bg-success/10 px-3 py-2 text-body-sm text-success-text">No next follow-up needed. Raise the order on the quotation next — the deal moves to Order Confirmed with the order, and to Won when it is dispatched.</p>
        )}
        {outcome === 'not_interested' && (
          <div className="rounded-lg border border-danger/25 bg-danger/5 p-3">
            <label className="block">
              <span className="mb-1.5 block text-body-sm font-semibold text-on-surface">Why was it lost? <span className="text-danger-text">*</span></span>
              <select value={lostReasonId} onChange={(e) => { setLostReasonId(e.target.value); setError(null); }}
                className="h-9 w-full rounded-lg border border-input bg-surface px-3 text-body-sm text-on-surface outline-none focus:border-primary">
                <option value="">{lostReasons.isLoading ? 'Loading reasons…' : 'Select…'}</option>
                {(lostReasons.data?.items ?? []).map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </label>
            <p className="mt-2 text-body-sm text-danger-text">This marks the business <b>Lost</b>, with this reason and what you write above (at least 10 characters — "close" is not accepted).</p>
          </div>
        )}
        {error && <p className="text-body-sm font-medium text-danger-text">{error}</p>}
      </div>
    </Modal>
  );
}

export default CloseFollowupDialog;
